import React, { useEffect } from 'react';
import type { ReactNode } from 'react';
import './ScrollFixes.css';

interface ScrollableContainerProps {
  children: ReactNode;
}

const ScrollableContainer: React.FC<ScrollableContainerProps> = ({ children }) => {
  useEffect(() => {
    // Force scrolling on mount
    const enableScroll = () => {
      window.scrollTo(0, 0);
      // Only allow scrolling on the root document, not on inner containers
      document.documentElement.style.overflow = 'auto';
      document.documentElement.style.overflowX = 'hidden';
      document.body.style.overflow = 'hidden'; // Changed from auto to hidden
      document.body.style.overflowX = 'hidden';
      
      // Force repaint to ensure scrolling works
      const scrollTop = document.documentElement.scrollTop;
      document.documentElement.scrollTop = scrollTop + 1;
      setTimeout(() => {
        document.documentElement.scrollTop = scrollTop;
      }, 0);
      
      // Ensure the container takes at least full viewport height
      const container = document.getElementById('landing-page-container');
      if (container) {
        const viewportHeight = window.innerHeight;
        container.style.minHeight = `${viewportHeight}px`;
      }
    };

    // Apply immediately and after a delay to ensure it works
    enableScroll();
    
    // Apply multiple times with increasing delays to ensure it works
    const timeoutIds = [
      setTimeout(enableScroll, 100),
      setTimeout(enableScroll, 500),
      setTimeout(enableScroll, 1000)
    ];
    
    // Also handle resize events
    window.addEventListener('resize', enableScroll);
    
    // Handle orientation change for mobile
    window.addEventListener('orientationchange', () => {
      setTimeout(enableScroll, 300);
    });
    
    // Handle visibility change (when tab gets focus)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        setTimeout(enableScroll, 300);
      }
    });

    return () => {
      timeoutIds.forEach(clearTimeout);
      window.removeEventListener('resize', enableScroll);
      window.removeEventListener('orientationchange', enableScroll);
      document.removeEventListener('visibilitychange', enableScroll);
    };
  }, []);

  return (
    <div 
      className="scrollable-container"
      style={{
        width: '100%',
        height: 'auto',
        minHeight: '100vh', // Ensure at least viewport height
        overflow: 'hidden', // Changed from auto to hidden
        overflowX: 'hidden',
        display: 'block',
        position: 'relative'
      }}
    >
      {children}
    </div>
  );
};

export default ScrollableContainer;
